import React, { cloneElement, useState, useEffect, useContext } from 'react';
import { connect } from 'react-redux';

import { AuthContext } from '../../context/AuthContext';

import ButtonSecondary from '../../components/ui/ButtonSecondary';
import ButtonPrimary from '../../components/ui/ButtonPrimary';
import BaseTable from '../../components/BaseTable';
import CreateNewEmployeeModal from './CreateNewEmployeeModal';
import BaseSearchBar from '../../components/BaseSearchBar';
import ResetPassword from '../../components/ResetPassword';
import DeleteEmployeeModal from '../EmployeeInfo/DeleteEmployeeModal';
import ChangePasswordModal from '../EmployeeInfo/ChangePasswordModal';

import {
  fetchAllEmployees,
  addEmployeeSort,
  fetchEmployeesBySearchTerm,
  clearEmployeeSearch,
  updateUserStatus,
  updateUserRole
} from '../../store/actions';

import './index.css';
import { sortText } from '../../utils/sortHelper';

const Employees = (props) => {
  const {role} = useContext(AuthContext);
  const initialLocalEmployees = props.searchTerm ? props.searchedEmployeeList : props.employeeList;
  const [localEmployees, setLocalEmployees] = useState(initialLocalEmployees);
  const [isShowCreateNewEmployee, setIsShowCreateNewEmployee] = useState(false);
  const [searchTerm, setSearchTerm] = useState(props.searchTerm);
  const [isShowDeleteEmployeeModal, setIsShowDeleteEmployeeModal] = useState(null);
  const [isShowChangePasswordModal, setIsShowChangePasswordModal] = useState(null);
  // console.log(role)
  useEffect(() => {
    !props.employeeList.length && props.fetchAllEmployees();
  }, []);

  const choseSort = (column) => {
    if (!column.isSortable) return;
    const { employeeSort } = props;
    props.addEmployeeSort(column.dataKey, employeeSort.prop === column.dataKey ? !employeeSort.isDesc : true);
  }

  const toggleCreateNewEmployee = () => {
    setIsShowCreateNewEmployee(!isShowCreateNewEmployee);
  }

  const searchEmployees = () => {
    props.fetchEmployeesBySearchTerm(searchTerm);
  }

  const clearEmployeeSearch = () => {
    setSearchTerm('');
    props.clearEmployeeSearch();
  }

  const changeSearchTerm = (e) => {
    setSearchTerm(e.target.value);
    !e.target.value && props.clearEmployeeSearch();
  }

  const selectStatus = (employee, value, prop) => {
    if (prop === 'status') {
      props.updateUserStatus(employee.UUID, value)
    } else {
      props.updateUserRole(employee.UUID, value)
    }
  }

  const toggleDeleteEmployeeModal = (emp) => {
    setIsShowDeleteEmployeeModal(emp ? emp : null);
  }

  const toggleChangePasswordModal = (emp) => {
    setIsShowChangePasswordModal(emp ? emp : null);
  }

  const tableOptions = {
    columns: [
      {
        name: "First Name",
        dataKey: 'firstName',
        type: 'text',
        isSortable: true,
      },
      {
        name: "Last Name",
        dataKey: 'lastName',
        type: 'text',
        isSortable: true,
      },
      {
        name: "Phone",
        dataKey: 'phone',
        type: 'text',
        isSortable: true,
      },
      {
        name: "Role",
        dataKey: 'role',
        type: 'select',
        isSortable: true,
        isSelectColored: true,
        selectOptions: [
          {'manager': null},
          {'standard': null}
        ],
        options: [
          'manager',
          'admin',
          'standard',
          'other'
          ],
        key: 1,
        minWidth: '7rem',
        isReadOnly: role !== 'admin'
      },
      {
        name: "Status",
        dataKey: 'status',
        type: 'select',
        isSortable: true,
        isSelectColored: true,
        selectOptions: [
          {'active': null},
          {'suspended': null},
          {'deleted': null}
        ],
        options: [
          'active',
          'inactive',
          'removed',
          'other',
          ],
        key: 2,
        minWidth: '7rem',
        isReadOnly: role !== 'admin'
      },
      {
        name: "Password",
        dataKey: 'name',
        type: 'component',
        component: cloneElement(<ResetPassword />, {
          resetPassword: setIsShowChangePasswordModal,
          role
        }),
        isSortable: false,
      }
    ]
  }

  const actions = {
    delete: (empToDelete) => {
      toggleDeleteEmployeeModal(empToDelete)
    }
  }

  useEffect(() => {
    const {employeeList, employeeSort, searchedEmployeeList, searchTerm} = props;
    if (!employeeList.length && !searchedEmployeeList.length) {
      return setLocalEmployees([]);
    }
    const {prop, isDesc} = employeeSort;
    const activeEmployeeList = searchTerm ? [...searchedEmployeeList] : [...employeeList];
    const sorted = activeEmployeeList.sort(sortText(prop, isDesc));
    setLocalEmployees(sorted);
  }, [props.employeeList, props.employeeSort, props.searchedEmployeeList]);

  return (
    <div className="employees">
      <header className="header">
        <h2 className="header__title">Employees</h2>
        <BaseSearchBar
          placeholder="Search by whatever"
          className="employees__search-bar"
          value={searchTerm}
          onChange={changeSearchTerm}
          onClear={clearEmployeeSearch}
          onSearch={searchEmployees}
        />
        <ButtonSecondary
          name="Search"
          onClick={searchEmployees}
        />
        {
          role === 'admin' &&
          <ButtonPrimary
            name="Add New Employee"
            onClick={toggleCreateNewEmployee}
          />
        }
      </header>
      <div className="table">
        <BaseTable
          onSort={choseSort}
          data={localEmployees}
          options={tableOptions}
          className="employees__table"
          rowOnClick={(id) => props.history.push(`/employees/${id}`)}
          onSelectStatus={selectStatus}
          actions={actions}
        />
      </div>
      {
        isShowCreateNewEmployee &&
        <CreateNewEmployeeModal
          onClose={toggleCreateNewEmployee}
        />
      }
      {isShowDeleteEmployeeModal &&
      <DeleteEmployeeModal
        onClose={() => toggleDeleteEmployeeModal(null)}
        title={isShowDeleteEmployeeModal.firstName + ' ' + isShowDeleteEmployeeModal.lastName}
        employee={isShowDeleteEmployeeModal}
      />}
      {isShowChangePasswordModal &&
      <ChangePasswordModal
        onClose={() => toggleChangePasswordModal(null)}
        title="Password"
        employee={isShowChangePasswordModal}
      />}
    </div>
  );
}

const mapStateToProps = ({
  employees:
    {
      employeeList,
      shortEmployeeList,
      employeeSort,
      employeeSearch: {
        searchedEmployeeList,
        searchTerm
      }
    }
}) => ({
  employeeList,
  shortEmployeeList,
  employeeSort,
  searchedEmployeeList,
  searchTerm
});

export default connect(mapStateToProps, {
  fetchAllEmployees,
  addEmployeeSort,
  fetchEmployeesBySearchTerm,
  clearEmployeeSearch,
  updateUserStatus,
  updateUserRole
})(Employees);
